import { cn } from '@/app/utils/cn';
import customRenderers from '@/components/block-editor';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';

const bgColorClasses = {
    blue100: 'bg-blue-100',
    blue200: 'bg-blue-200',
    blue300: 'bg-blue-300',
    blue400: 'bg-blue-400',
    blue500: 'bg-blue-500',
    blue600: 'bg-blue-600',
    blue700: 'bg-blue-700',
    blue800: 'bg-blue-800',
    indigo100: 'bg-indigo-100',
    indigo200: 'bg-indigo-200',
    indigo300: 'bg-indigo-300',
    indigo400: 'bg-indigo-400',
    indigo500: 'bg-indigo-500',
    indigo600: 'bg-indigo-600',
    indigo700: 'bg-indigo-700',
    indigo800: 'bg-indigo-800',
    orange100: 'bg-orange-100',
    orange200: 'bg-orange-200',
    orange300: 'bg-orange-300',
    orange400: 'bg-orange-400',
    orange500: 'bg-orange-500',
    orange600: 'bg-orange-600',
    orange700: 'bg-orange-700',
    orange800: 'bg-orange-800',
    green100: 'bg-green-100',
    green200: 'bg-green-200',
    green300: 'bg-green-300',
    green400: 'bg-green-400',
    green500: 'bg-green-500',
    green600: 'bg-green-600',
    green700: 'bg-green-700',
    green800: 'bg-green-800',
    yellow100: 'bg-yellow-100',
    yellow200: 'bg-yellow-200',
    yellow300: 'bg-yellow-300',
    yellow400: 'bg-yellow-400',
    yellow500: 'bg-yellow-500',
    yellow600: 'bg-yellow-600',
    yellow700: 'bg-yellow-700',
    yellow800: 'bg-yellow-800',
    cyan100: 'bg-cyan-100',
    cyan200: 'bg-cyan-200',
    cyan300: 'bg-cyan-300',
    cyan400: 'bg-cyan-400',
    cyan500: 'bg-cyan-500',
    cyan600: 'bg-cyan-600',
    cyan700: 'bg-cyan-700',
    cyan800: 'bg-cyan-800',
    fuschia100: 'bg-fuschia-100',
    fuschia200: 'bg-fuschia-200',
    fuschia300: 'bg-fuschia-300',
    fuschia400: 'bg-fuschia-400',
    fuschia500: 'bg-fuschia-500',
    fuschia600: 'bg-fuschia-600',
    fuschia700: 'bg-fuschia-700',
    fuschia800: 'bg-fuschia-800'
};

const colorPaletteClasses = {
    blue100: 'text-blue-100',
    blue200: 'text-blue-200',
    blue300: 'text-blue-300',
    blue400: 'text-blue-400',
    blue500: 'text-blue-500',
    blue600: 'text-blue-600',
    blue700: 'text-blue-700',
    blue800: 'text-blue-800',
    indigo100: 'text-indigo-100',
    indigo200: 'text-indigo-200',
    indigo300: 'text-indigo-300',
    indigo400: 'text-indigo-400',
    indigo500: 'text-indigo-500',
    indigo600: 'text-indigo-600',
    indigo700: 'text-indigo-700',
    indigo800: 'text-indigo-800',
    orange100: 'text-orange-100',
    orange200: 'text-orange-200',
    orange300: 'text-orange-300',
    orange400: 'text-orange-400',
    orange500: 'text-orange-500',
    orange600: 'text-orange-600',
    orange700: 'text-orange-700',
    orange800: 'text-orange-800',
    green100: 'text-green-100',
    green200: 'text-green-200',
    green300: 'text-green-300',
    green400: 'text-green-400',
    green500: 'text-green-500',
    green600: 'text-green-600',
    green700: 'text-green-700',
    green800: 'text-green-800',
    yellow100: 'text-yellow-100',
    yellow200: 'text-yellow-200',
    yellow300: 'text-yellow-300',
    yellow400: 'text-yellow-400',
    yellow500: 'text-yellow-500',
    yellow600: 'text-yellow-600',
    yellow700: 'text-yellow-700',
    yellow800: 'text-yellow-800',
    cyan100: 'text-cyan-100',
    cyan200: 'text-cyan-200',
    cyan300: 'text-cyan-300',
    cyan400: 'text-cyan-400',
    cyan500: 'text-cyan-500',
    cyan600: 'text-cyan-600',
    cyan700: 'text-cyan-700',
    cyan800: 'text-cyan-800',
    fuschia100: 'text-fuschia-100',
    fuschia200: 'text-fuschia-200',
    fuschia300: 'text-fuschia-300',
    fuschia400: 'text-fuschia-400',
    fuschia500: 'text-fuschia-500',
    fuschia600: 'text-fuschia-600',
    fuschia700: 'text-fuschia-700',
    fuschia800: 'text-fuschia-800'
};

const spacingClasses = {
    nospace: '',
    small: 'spacing-small',
    medium: 'spacing-medium',
    large: 'spacing-large'
};

const innerSpacingClasses = {
    nospace: '',
    small: 'px-2',
    medium: 'px-4',
    large: 'px-6'
};

const weightClasses = {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold'
};

const letterSpacingClasses = {
    tight: 'tracking-tight',
    normal: 'tracking-normal',
    wide: 'tracking-wide',
    wider: 'tracking-wider',
    widest: 'tracking-widest'
};

const fontSizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
    '5xl': 'text-5xl'
};

const lineHeightClasses = {
    none: 'leading-none',
    tight: 'leading-tight',
    snug: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose'
};

function SuperParagraph({
    innerSpace = 'nospace',
    emptyParagraph,
    paragraphSpacing = 'nospace',
    customPageBlocks = customRenderers,
    color,
    bgColor,
    weight = 'normal',
    letterSpacing = 'normal',
    fontSize = 'base',
    lineHeight = 'normal',
    text
}) {
    return (
        <div
            className={cn(
                spacingClasses[paragraphSpacing],
                innerSpacingClasses[innerSpace],
                color && colorPaletteClasses[color],
                bgColor && bgColorClasses[bgColor],
                weightClasses[weight],
                letterSpacingClasses[letterSpacing],
                fontSizeClasses[fontSize],
                lineHeightClasses[lineHeight],
                'not-prose'
            )}>
            {!Boolean(emptyParagraph) && (
                <DotBlockEditor customRenderers={customPageBlocks} blocks={text} />
            )}
        </div>
    );
}

export default SuperParagraph;
